import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ConfirmationDialog from "./ConfirmationDialog";

const getInitialFormData = (selectedPathology) => ({
  pathologie: `Copy of ${selectedPathology?.pathologie || ""}`.trim(),
  max_number_of_rules: selectedPathology?.max_number_of_rules ?? "",
  age_specifique_pathologie: selectedPathology?.age_specifique_pathologie || "",
  sexe_specifique_pathologie: selectedPathology?.sexe_specifique_pathologie || "",
  patho_associées: selectedPathology?.patho_associées || "",
  red_flags: selectedPathology?.red_flags || "",
  green_flags: selectedPathology?.green_flags || "",
  min_age_pathologie: selectedPathology?.min_age_pathologie || "",
  max_age_pathologie: selectedPathology?.max_age_pathologie || "",
  appareilId: selectedPathology?.appareilId?._id || selectedPathology?.appareilId || "",
});

const DuplicatePathologyDialog = ({ isOpen, togglePopup, selectedPathology, onDuplicateSuccess }) => {
  const [formData, setFormData] = useState(getInitialFormData(selectedPathology));
  const [appareils, setAppareils] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedVariantIds, setSelectedVariantIds] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  
  // Toast notification states
  const [toastOpen, setToastOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen || !selectedPathology) {
      return;
    }

    setFormData(getInitialFormData(selectedPathology));
    setErrorMessage("");
    setToastOpen(false);
  }, [isOpen, selectedPathology]);

  useEffect(() => {
    if (!isOpen || !selectedPathology) {
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    const fetchAppareils = async () => {
      try {
        const response = await axios.get("/appareils", {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });
        setAppareils(response.data);
      } catch (error) {
        console.error("Error fetching appareils:", error);
      }
    };

    const fetchVariants = async () => {
      setLoadingVariants(true);
      try {
        const response = await axios.get(`/pathologies/${selectedPathology._id}/variants`, {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        });
        setVariants(response.data);
        setSelectedVariantIds(response.data.map((variant) => variant._id));
      } catch (error) {
        setErrorMessage(error.response?.data?.message || "Error fetching variants");
        setToastOpen(true);
      } finally {
        setLoadingVariants(false);
      }
    };

    fetchAppareils();
    fetchVariants();
  }, [isOpen, selectedPathology]);

  const groupedVariants = useMemo(() => {
    const groups = new Map();

    variants.forEach((variant) => {
      const medicament = variant.medicamentId || {};
      const medicamentKey = medicament._id || "unknown-medicament";
      const medicamentLabel = medicament.specialite || medicament.medoc_ou_complement_ou_phyto || "Médicament sans nom";

      if (!groups.has(medicamentKey)) {
        groups.set(medicamentKey, {
          medicamentId: medicamentKey,
          medicamentLabel,
          variants: [],
        });
      }

      groups.get(medicamentKey).variants.push(variant);
    });

    const output = Array.from(groups.values());
    setExpandedAccordions(output.map(g => g.medicamentId));
    return output;
  }, [variants]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    if (errorMessage) {
      setErrorMessage("");
      setToastOpen(false);
    }
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const handleVariantToggle = (variantId) => {
    setSelectedVariantIds((prevIds) => (
      prevIds.includes(variantId)
        ? prevIds.filter((id) => id !== variantId)
        : [...prevIds, variantId]
    ));
  };

  const handleAccordionToggle = (panelId) => (event, isExpanded) => {
    setExpandedAccordions((prev) =>
      isExpanded ? [...prev, panelId] : prev.filter((id) => id !== panelId)
    );
  };

  const handleSelectAll = () => {
    setSelectedVariantIds(variants.map((v) => v._id));
  };

  const handleDeselectAll = () => {
    setSelectedVariantIds([]);
  };

  const handleExpandAll = () => {
    setExpandedAccordions(groupedVariants.map((g) => g.medicamentId));
  };

  const handleCollapseAll = () => {
    setExpandedAccordions([]);
  };

  const handleOpenConfirm = () => {
    if (!formData.pathologie.trim()) {
      setErrorMessage("Nom de la pathologie est requis.");
      setToastOpen(true);
      return;
    }

    if (!formData.appareilId) {
      setErrorMessage("L'appareil est requis.");
      setToastOpen(true);
      return;
    }

    if (selectedVariantIds.length === 0) {
      setErrorMessage("Veuillez sélectionner au moins un variant.");
      setToastOpen(true);
      return;
    }

    setErrorMessage("");
    setToastOpen(false);
    setConfirmOpen(true);
  };

  const handleDuplicate = async () => {
    if (!selectedPathology) {
      return;
    }

    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    try {
      const response = await axios.post(
        `/pathologies/${selectedPathology._id}/duplicate`,
        {
          ...formData,
          selectedVariantIds,
        },
        {
          headers: {
            "x-access-token": token,
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setConfirmOpen(false);
      togglePopup();

      if (onDuplicateSuccess) {
        onDuplicateSuccess(response.data.data);
      }
    } catch (error) {
      setConfirmOpen(false);

      const rawMessage = error.response?.data?.message || "";

      // Intercept and translate backend uniqueness constraints into French
      if (
        error.response?.status === 409 || 
        rawMessage.includes("already exists") || 
        rawMessage.toLowerCase().includes("same name and appareil")
      ) {
        setErrorMessage("Une pathologie avec le même nom et le même appareil existe déjà.");
      } else if (error.response?.status === 400 && rawMessage.includes("required")) {
        setErrorMessage("Le nom de la pathologie et l'appareil de destination sont requis.");
      } else {
        setErrorMessage(rawMessage || "Une erreur inattendue est survenue lors de la duplication.");
      }
      
      setToastOpen(true);
    }
  };

  if (!isOpen || !selectedPathology) {
    return null;
  }

  const allSelected = selectedVariantIds.length === variants.length;
  const allExpanded = expandedAccordions.length === groupedVariants.length;
  const hasDuplicateError = !!errorMessage && errorMessage.includes("existe déjà");

  return (
    <>
      <Dialog open={isOpen} onClose={togglePopup} maxWidth="md" fullWidth>
        <DialogTitle>Dupliquer la pathologie</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nom de la pathologie"
              name="pathologie"
              value={formData.pathologie}
              onChange={handleChange}
              fullWidth
              error={hasDuplicateError}
            />
            <TextField
              label="Nombre max de règles"
              name="max_number_of_rules"
              type="number"
              value={formData.max_number_of_rules}
              onChange={handleChange}
              fullWidth
              inputProps={{ min: 0, step: 1 }}
            />
            <FormControl fullWidth>
              <InputLabel id="duplicate-pathology-age-label">Age spécifique</InputLabel>
              <Select
                labelId="duplicate-pathology-age-label"
                name="age_specifique_pathologie"
                value={formData.age_specifique_pathologie}
                label="Age spécifique"
                onChange={handleChange}
              >
                <MenuItem value="">Aucun</MenuItem>
                <MenuItem value="A">Adulte</MenuItem>
                <MenuItem value="E">Enfant</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="duplicate-pathology-sex-label">Sexe spécifique</InputLabel>
              <Select
                labelId="duplicate-pathology-sex-label"
                name="sexe_specifique_pathologie"
                value={formData.sexe_specifique_pathologie}
                label="Sexe spécifique"
                onChange={handleChange}
              >
                <MenuItem value="">Aucun</MenuItem>
                <MenuItem value="H">Homme</MenuItem>
                <MenuItem value="F">Femme</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Pathologies associées"
              name="patho_associées"
              value={formData.patho_associées}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Red flags"
              name="red_flags"
              value={formData.red_flags}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Green flags"
              name="green_flags"
              value={formData.green_flags}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Min age"
              name="min_age_pathologie"
              value={formData.min_age_pathologie}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Max age"
              name="max_age_pathologie"
              value={formData.max_age_pathologie}
              onChange={handleChange}
              fullWidth
            />
            <FormControl fullWidth error={hasDuplicateError}>
              <InputLabel id="duplicate-pathology-appareil-label">Appareil</InputLabel>
              <Select
                labelId="duplicate-pathology-appareil-label"
                name="appareilId"
                value={formData.appareilId}
                label="Appareil"
                onChange={handleChange}
              >
                <MenuItem value="">Choisir Appareil</MenuItem>
                {appareils.map((appareil) => (
                  <MenuItem key={appareil._id} value={appareil._id}>
                    {appareil.appareil}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Catchy Control Center Header Section */}
            <Box>
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ 
                  mb: 2, 
                  mt: 1,
                  pb: 1, 
                  borderBottom: "1px solid", 
                  borderColor: "divider" 
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
                    Variants à dupliquer
                  </Typography>
                  <Chip 
                    label={`${selectedVariantIds.length} sélectionné${selectedVariantIds.length > 1 ? 's' : ''}`}
                    color={selectedVariantIds.length > 0 ? "primary" : "default"}
                    size="small"
                    variant={selectedVariantIds.length > 0 ? "filled" : "outlined"}
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                
                {!loadingVariants && variants.length > 0 && (
                  <Stack direction="row" spacing={1}>
                    <Button 
                      size="small" 
                      variant="text"
                      color={allSelected ? "warning" : "primary"}
                      startIcon={allSelected ? <CheckBoxOutlineBlankIcon /> : <CheckBoxIcon />}
                      onClick={allSelected ? handleDeselectAll : handleSelectAll}
                      sx={{ fontWeight: 600, textTransform: "none" }}
                    >
                      {allSelected ? "Désélectionner tout" : "Sélectionner tout"}
                    </Button>
                    <Button 
                      size="small" 
                      variant="text"
                      color="inherit"
                      startIcon={allExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                      onClick={allExpanded ? handleCollapseAll : handleExpandAll}
                      sx={{ fontWeight: 600, textTransform: "none", color: "text.secondary" }}
                    >
                      {allExpanded ? "Tout réduire" : "Tout développer"}
                    </Button>
                  </Stack>
                )}
              </Stack>

              {loadingVariants ? (
                <Typography variant="body2" color="text.secondary">
                  Chargement des variants...
                </Typography>
              ) : (
                groupedVariants.map((group) => (
                  <Accordion 
                    key={group.medicamentId} 
                    expanded={expandedAccordions.includes(group.medicamentId)}
                    onChange={handleAccordionToggle(group.medicamentId)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>
                        {group.medicamentLabel}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {group.variants.map((variant) => (
                          <FormControlLabel
                            key={variant._id}
                            control={
                              <Checkbox
                                checked={selectedVariantIds.includes(variant._id)}
                                onChange={() => handleVariantToggle(variant._id)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {variant.medoc_ou_complement_ou_phyto || "Variant sans nom"}
                                {variant.dose ? ` - Dose: ${variant.dose}` : ""}
                                {variant.frequence ? ` - Fréquence: ${variant.frequence}` : ""}
                              </Typography>
                            }
                          />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          </Stack>
        </DialogContent>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
          <Button onClick={togglePopup} variant="outlined">
            Annuler
          </Button>
          <Button onClick={handleOpenConfirm} variant="contained">
            Dupliquer
          </Button>
        </Box>
      </Dialog>

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDuplicate}
        title="Confirmer la duplication"
        message={
          <span>
            Êtes-vous sûr de vouloir dupliquer la pathologie <b>{selectedPathology.pathologie}</b> avec <b>{selectedVariantIds.length}</b> variants sélectionnés ?
          </span>
        }
        confirmText="Dupliquer"
        confirmColor="primary"
      />

      {/* Global Toast Error Notification System */}
      <Snackbar 
        open={toastOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity="error" variant="filled" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DuplicatePathologyDialog;